import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import CloseIcon from '@material-ui/icons/Close'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import { useElementTreeStore } from 'components/ElementsTree/ElementsTreeStore'
import { leftMostPaneStyles } from 'components/ElementsTree/styles/leftMostPaneStyles'
import { CategoryPanel } from 'models/category'
import { ElementPanelSubItem } from 'models/projectElements'
import { saveToLocalStorage } from 'pages/ProjectsPage/WorkspacePage'
import { NumberSize, Resizable, ResizeDirection } from 're-resizable'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { getFromLocalStorage } from 'shared/utils/workspaceUtils'

import { IconButton } from '@material-ui/core'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import CollapseIcon from '../CategoryPanel/CollapseIcon'
import ExpandLeftPaneIcon from './ExpandLeftPaneIcon'
import { LeftMostPaneButtonContainer } from './LeftMostPaneButtonContainer'
import { LeftMostPaneInnerContainer } from './LeftMostPaneInnerContainer'

const COLLAPSED_PANEL_WIDTH = 40
const DEFAULT_PANEL_WIDTH = 323
const MAXIMUM_PANEL_WIDTH = 468
export interface LeftPaneStyleProps {
    showLeftMostPane: boolean
    currentPane?: string
    outerPanelWidth?: number
}
interface ResizeStopParams {
    event: MouseEvent | TouchEvent
    direction: ResizeDirection
    ref: HTMLDivElement | HTMLElement
    delta: NumberSize
}
type OnResizeHandler = (params: ResizeStopParams) => void
export type TogglePanelType = 'category' | 'elements'
export interface LeftMostPaneProps {
    /** React node representing the category section. */
    categoryNode?: React.ReactNode

    /** React node representing the elements section. */
    elementsNode?: React.ReactNode

    /** React node representing the search bar. */
    searchBar?: React.ReactNode

    /** Handles collapsing the left panel. */
    handleCollapseLeftPanel?: VoidFunction

    /** Handles changes in grouping selection. */
    handleChangeSelectGroupping?: (value: ElementPanelSubItem) => void

    /** Currently selected element filter type. */
    selectedElementFilterType?: ElementPanelSubItem

    /** Handles changes in the selected filter. */
    handleChangeSelectFilter?: (value: string) => void

    /** Handles changes in filter options. */
    handleChangeFilterOption?: (value: ElementPanelSubItem) => void

    /** Currently selected category, if any. */
    selectedCategory?: CategoryPanel | null

    /** Callback invoked when a category is removed from the filter pane. */
    handleResetCategories: VoidFunction

    /** Indicates whether a search has been initiated. */
    isSearchInitiated?: boolean
}

/*
TODO - Move properties from workspacepage to leftmostpane once old elements panel becomes obsolete
*/
export const LeftMostPane = ({
    categoryNode,
    elementsNode,
    searchBar,
    handleCollapseLeftPanel,
    handleChangeSelectGroupping,
    selectedElementFilterType,
    handleChangeFilterOption,

    selectedCategory,

    handleResetCategories,
    isSearchInitiated,
}: LeftMostPaneProps) => {
    const { currentUserInfo } = useReactiveVar(userInfo)
    const { kpUserId: userId } = currentUserInfo
    const contentTeamFromStudioPage = isReactiveContentTeamFromStudioPage()
        ?.value
    const { projectId } = useParams<{ projectId?: string }>()
    const updateElementPanelState = useElementTreeStore(
        (store) => store.updateElementPanelState
    )
    const showLeftMostPane = useElementTreeStore(
        (store) => store.showLeftMostPane
    )
    const setUpdateElementPanelState = useElementTreeStore(
        (store) => store.setUpdateElementPanelState
    )
    const [outerPanelWidth, setOuterPanelWidth] = useState<number>(
        (showLeftMostPane ? DEFAULT_PANEL_WIDTH : COLLAPSED_PANEL_WIDTH) ?? 0
    )
    const { previewFeatures } = usePreviewFeatures()
    const currentPane = useElementTreeStore((store) => store.currentPane)
    const setCurrentPane = useElementTreeStore((store) => store.setCurrentPane)
    const classes = leftMostPaneStyles({
        showLeftMostPane,
        currentPane,
        outerPanelWidth: outerPanelWidth! - COLLAPSED_PANEL_WIDTH,
    })
    const updatePersistentStateValues = React.useCallback(
        (propKey: string, inputValue: boolean | any) => {
            saveToLocalStorage(propKey, inputValue, userId, projectId)
        },
        [userId, projectId]
    )
    const expandIcon = React.useMemo(() => {
        return (
            <HoverTooltip
                className={clsx(
                    classes.expandCollapseIcon,
                    showLeftMostPane
                        ? classes.collapseIconContainer
                        : classes.expandIconContainer
                )}
                text={showLeftMostPane ? 'Collapse Panel' : 'Expand Panel'}
                placements={['right']}
            >
                <div
                    data-testid="expandIconLeftPane"
                    onClick={handleCollapseLeftPanel}
                >
                    {' '}
                    <ExpandLeftPaneIcon
                        className={
                            !showLeftMostPane ? classes.expandIconLeftPane : ''
                        }
                    />{' '}
                </div>
            </HoverTooltip>
        )
    }, [handleCollapseLeftPanel])
    const changeTab = React.useCallback(
        (tab: string) => {
            setCurrentPane(tab)
            if (!showLeftMostPane) {
                handleCollapseLeftPanel?.()
            }
            setOuterPanelWidth(323)
            updatePersistentStateValues('selectedLeftMostPaneTab', tab)
            if (updateElementPanelState) {
                setUpdateElementPanelState?.(false)
            }
        },
        [
            setCurrentPane,
            handleCollapseLeftPanel,
            showLeftMostPane,
            setUpdateElementPanelState,
        ]
    )
    // handler that uses destructuring
    const onResizeHandler = React.useCallback<OnResizeHandler>(
        ({ ref }) => {
            const updatedPanelWidth = Number(ref.style.width.split('px')[0])
            setOuterPanelWidth(updatedPanelWidth)
        },
        [setOuterPanelWidth]
    )
    const onResize = React.useCallback(
        (
            event: MouseEvent | TouchEvent,
            direction: ResizeDirection,
            ref: HTMLDivElement | HTMLElement,
            delta: NumberSize
        ) => {
            onResizeHandler({ event, direction, ref, delta })
        },
        [onResizeHandler]
    )

    /** Effect: Initialize currentPane from local storage or content team flag */
    React.useEffect(() => {
        if (contentTeamFromStudioPage) {
            setCurrentPane('elements')
        } else {
            const selectedLeftPaneTab = getFromLocalStorage(
                userId ?? '',
                projectId!,
                'selectedLeftMostPaneTab'
            )

            setCurrentPane(
                selectedLeftPaneTab ? selectedLeftPaneTab : 'elements'
            )
        }
    }, [projectId, userId])

    /** Effect: change outer panel width if panel collapse */
    React.useEffect(() => {
        if (!showLeftMostPane) {
            setOuterPanelWidth(COLLAPSED_PANEL_WIDTH)
        }
    }, [showLeftMostPane])

    /** Effect: Change tab to 'elements' if category selection changes */
    React.useEffect(() => {
        if (
            selectedCategory?.id !== 'all' &&
            updateElementPanelState &&
            currentPane !== 'elements'
        ) {
            changeTab('elements')
            handleChangeSelectGroupping?.({
                ...(selectedElementFilterType as ElementPanelSubItem),
                type: 'ASSEMBLIES',
            })
            setUpdateElementPanelState?.(false)
        }
    }, [
        selectedCategory,
        updateElementPanelState,
        handleChangeSelectGroupping,
        selectedElementFilterType,
        currentPane,
    ])
    return (
        <>
            <Resizable
                size={{ height: 797, width: outerPanelWidth }}
                data-testid="leftMostPane"
                className={classes.resizable}
                onResizeStop={onResize}
                enable={{
                    right: true,
                }}
                maxWidth={MAXIMUM_PANEL_WIDTH}
                minWidth={
                    !showLeftMostPane
                        ? COLLAPSED_PANEL_WIDTH
                        : DEFAULT_PANEL_WIDTH
                }
            >
                <div
                    className={clsx(classes.fullWidthLeftPaneKeynotesContainer)}
                >
                    {showLeftMostPane &&
                        currentPane === 'elements' &&
                        (previewFeatures.hasExtendedView ? (
                            <IconButton
                                className={classes.closeButtonBeta}
                                data-testid="closePanelBeta"
                                onClick={handleCollapseLeftPanel}
                                title="Collapse panel"
                                disableRipple
                                disableFocusRipple
                                aria-label="Collapse panel"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        ) : (
                            <HoverTooltip
                                className={classes.collapseIcon}
                                text="Collapse Panel"
                                placements={['left']}
                            >
                                <div
                                    data-testid="collapseIconLeftPane"
                                    onClick={handleCollapseLeftPanel}
                                >
                                    {' '}
                                    <CollapseIcon />{' '}
                                </div>
                            </HoverTooltip>
                        ))}
                    <LeftMostPaneButtonContainer
                        changeTab={changeTab}
                        contentTeamFromStudioPage={contentTeamFromStudioPage}
                        isDivisionSelected={
                            selectedElementFilterType?.type === 'DIVISIONS'
                        }
                    />
                    {showLeftMostPane && (
                        <LeftMostPaneInnerContainer
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            categoryNode={categoryNode}
                            elementsNode={elementsNode}
                            handleChangeSelectGroupping={
                                handleChangeSelectGroupping
                            }
                            selectedElementFilterType={
                                selectedElementFilterType
                            }
                            handleChangeFilterOption={handleChangeFilterOption}
                            selectedCategory={selectedCategory}
                            outerPanelWidth={outerPanelWidth}
                            handleResetCategories={handleResetCategories}
                            searchBar={searchBar}
                            isSearchInitiated={isSearchInitiated}
                        />
                    )}
                </div>
            </Resizable>
        </>
    )
}
